/**
 * Recovery from lazy-loaded chunks (route components, their CSS, the font loader) that fail to load.
 *
 * After a deploy, a tab still running the previous build asks the CDN for chunk hashes that are
 * gone. A hard reload fetches the fresh index.html and the current hashes. The same failures also
 * arrive in bursts from email link scanners, which open a link in a hidden tab and abandon it
 * mid-load (Rollbar #6 / #1590, burst from HubSpot `/go/` links on 2026-10-22); those are noise.
 *
 * Kept dependency-free so `rollbarConfig.ts`, which imports it, stays loadable under Vitest.
 */

const CHUNK_LOAD_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'Importing a module script failed', // Safari
  'error loading dynamically imported module', // Firefox
  'Unable to preload CSS', // Vite's preload helper, when a chunk's stylesheet fails
]

export function isChunkLoadErrorMessage(message: string): boolean {
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => message.includes(pattern))
}

const RELOAD_GUARD_KEY = 'vh:chunk-load-reload'

let unloading = false

/** True once the page has started going away: `pagehide` fired, or `reloadOnce` navigated. */
export function isPageUnloading(): boolean {
  return unloading
}

export function isPageHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

/**
 * Runs `reload` unless this tab already reloaded for a chunk failure since its last completed
 * navigation, so a chunk that is genuinely missing cannot reload the page in a loop. Returns
 * whether it ran. When sessionStorage is unavailable the guard cannot be recorded, so it declines
 * rather than risk that loop.
 */
export function reloadOnce(reload: () => void): boolean {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY))
      return false
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
  } catch {
    return false
  }

  unloading = true
  reload()

  return true
}

export function clearReloadGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // Storage blocked: reloadOnce never set the key either.
  }
}

export function installChunkLoadRecovery(): void {
  window.addEventListener('pagehide', () => {
    unloading = true
  })

  // A page restored from the back/forward cache is live again.
  window.addEventListener('pageshow', () => {
    unloading = false
  })

  // Vite dispatches this for every failed dynamic import or chunk stylesheet, including ones the
  // router never sees. The default is left alone on purpose: preventing it makes the import resolve
  // to `undefined`, which surfaces as a different, unrecognised error ("Couldn't resolve component")
  // instead of the chunk error that checkIgnore knows to drop while the page reloads.
  window.addEventListener('vite:preloadError', () => {
    if (isPageHidden() || !navigator.onLine)
      return

    // Deferred to a macrotask so that, for a failed route navigation, router.onError claims the
    // guard first and reloads into the route being navigated to rather than the one being left.
    setTimeout(() => reloadOnce(() => window.location.reload()))
  })
}
